<template>
  <div class="favorites-list d-flex flex-column align-items-start">
    <div class="d-flex justify-content-between w-100 align-items-center mb-3 gap-1 flex-wrap">
      <span class="title">
        {{ $t('Favourites', { favorites: translations.favorites}) }}
      </span>
      <button-component
        v-if="isLogged && favoriteList.length"
        @btn-click="toggleAllFavoriteSelection"
        :variant="allSelected ? 'main' : 'bordered'"
        class="text-nowrap"
        :title="$t('All Favourites', {
          favourites: translations.favorites
        })"
      >
        {{ $t('All Favourites', {favourites: translations.favorites}) }}
      </button-component>
    </div>
    <div v-if="isLogged" class="w-100 items-list">
      <template v-if="processing">
        <loader-component/>
      </template>
      <template v-else-if="favoriteList.length">
        <favorite-list-item
          v-for="favorite in favoriteList"
          :key="favorite.id"
          :favorite="favorite"
          @selected="favoriteSelectedChange"
          :selected="isSelected(favorite)"
        />
      </template>
      <div v-else>{{ $t('NoFavorites', {favorites: translations.favorites}) }}</div>
    </div>
    <div v-else class="d-flex flex-column w-100 align-items-start">
      {{ $t('LoginToFavouriting', {favoriting: translations.favoriting}) }}
      <app-link
        :to="$options.pages.LOGIN"
        class="login-link d-block mt-4 px-5 py-2 align-self-center"
      >
        {{ $t('Login') }}
      </app-link>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex';
import {GET_FAVORITE} from '@/store/actionTypes';
import FavoriteListItem from './FavoriteListItem';
import LoaderComponent from '../LoaderComponent';
import {FAVORITE_TYPES} from '@/const';
import ButtonComponent from '@/components/base-components/buttons/ButtonComponent';
import AppLink from '@/components/base-components/AppLink';
import {LOGIN} from '@/pages';

const SELECT_ONE_PER_CLICK = true;
const initialState = {
  [FAVORITE_TYPES.event]: {},
  [FAVORITE_TYPES.participant]: {},
  [FAVORITE_TYPES.category]: {},
  [FAVORITE_TYPES.group]: {},
};

export default {
  name: 'FavoritesList',
  pages: {LOGIN},
  components: {
    AppLink,
    ButtonComponent,
    LoaderComponent,
    FavoriteListItem,
  },
  data() {
    return {
      processing: false,
      allSelected: false,
      ...initialState,
    };
  },
  computed: {
    ...mapGetters({
      isLogged: 'isLogged',
    }),
    ...mapState({
      favoriteList: ({favorite}) => favorite.favorites,
    }),
    translations() {
      return this.$projectSettings.favorites.translates;
    },
    selected() {
      return {
        [FAVORITE_TYPES.event]: this[FAVORITE_TYPES.event],
        [FAVORITE_TYPES.participant]: this[FAVORITE_TYPES.participant],
        [FAVORITE_TYPES.category]: this[FAVORITE_TYPES.category],
        [FAVORITE_TYPES.group]: this[FAVORITE_TYPES.group],
      };
    },
  },
  watch: {
    selected() {
      !this.allSelected &&
      this.$emit('update:selectedFavorites', this.selected);
    },
    allSelected() {
      this.$emit('allFavorites', this.allSelected);
    },
  },
  methods: {
    ...mapActions({
      getFavorites: GET_FAVORITE,
    }),
    async toggleAllFavoriteSelection() {
      const selected = !this.allSelected;
      this.clearState();
      await this.$nextTick();
      this.allSelected = selected;
    },
    clearState() {
      Object.keys(initialState).forEach((name) => {
        this[name] = initialState[name];
      });
    },
    addNewFavorite(newFavorite) {
      SELECT_ONE_PER_CLICK && this.clearState();
      this[newFavorite.favoritable_type] = Object.assign(
          {},
          this[newFavorite.favoritable_type],
          {[newFavorite.favoritable_id]: newFavorite},
      );
    },
    favoriteSelectedChange(data) {
      this.allSelected = false;

      if (data.selected) {
        this.addNewFavorite(data);
      } else {
        delete this[data.favoritable_type][data.favoritable_id];
        this[data.favoritable_type] = Object.assign(
            {},
            this[data.favoritable_type],
        );
      }
    },
    isSelected({
      favoritable_type: type,
      favoritable_id: id,
    }) {
      return Boolean(this[type]?.[id]);
    },
  },
  mounted() {
    this.processing = true;
    this.isLogged &&
    this.getFavorites().finally(() => {
      this.processing = false;
    });
    this.$emit('interface', {
      clear: () => {
        this.clearState();
        this.allSelected = false;
      },
    });
  },
};
</script>

<style scoped lang="scss">
.favorites-list {
  text-transform: none;

  .title {
    font-size: 1.5rem;
    text-transform: capitalize;
  }

  .main {
    --background: var(--settings-body-text-color);
    --text: var(--settings-body-background);
    border: 1px solid var(--background);
  }
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.login-link {
  background-color: var(--settings-login-button-background);
  color: var(--settings-login-button-text-color);
  text-decoration: none;
  border-radius: 0.3rem;
}
</style>
